import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, Database, ChefHat, DollarSign,
  Calculator, TrendingDown, Percent, Upload, FileText,
  Pizza, Menu, X, Home, GitCompareArrows, Wand2, LogIn, UserPlus, User
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/wizard', label: 'Guided Setup', icon: Wand2 },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/ingredients', label: 'Ingredients', icon: Database },
  { path: '/recipes', label: 'Recipe Builder', icon: ChefHat },
  { path: '/compare', label: 'Compare', icon: GitCompareArrows },
  { path: '/labor', label: 'Labor Costs', icon: DollarSign },
  { path: '/break-even', label: 'Break-Even', icon: Calculator },
  { path: '/specials', label: 'Specials', icon: Percent },
  { path: '/upload', label: 'Upload Center', icon: Upload },
  { path: '/reports', label: 'Reports', icon: FileText },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const navContent = (
    <>
      {/* Logo */}
      <div className="p-4 border-b-2 border-[#2A2A2A]">
        <NavLink to="/" className="flex items-center gap-2" data-testid="sidebar-logo">
          <div className="w-10 h-10 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
            <Pizza className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="leading-tight">
              <p className="font-bold text-sm text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Mikey Mozzarella's
              </p>
              <p className="text-xs text-[#E23D28] font-semibold" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Pizza-lator
              </p>
            </div>
          )}
        </NavLink>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 p-3 space-y-1" data-testid="sidebar-nav">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const isActive = location.pathname === path;
          return (
            <NavLink
              key={path}
              to={path}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${path.slice(1)}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-[#E23D28] text-white border-2 border-[#2A2A2A] shadow-[3px_3px_0px_0px_rgba(42,42,42,1)]'
                  : 'text-[#2A2A2A] hover:bg-[#FFC107]/20 border-2 border-transparent'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User / Auth */}
      {!collapsed && (
        <div className="p-4 border-t-2 border-[#2A2A2A]">
          {user ? (
            <div className="text-center">
              <button onClick={() => { navigate('/profile'); setMobileOpen(false); }}
                className="flex items-center gap-2 mx-auto text-left hover:bg-[#FFC107]/20 rounded-lg px-2 py-1.5 transition-colors w-full"
                data-testid="sidebar-profile-link">
                <div className="w-7 h-7 bg-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#2A2A2A] truncate">{user.name || user.email}</p>
                  <p className="text-[10px] text-[#2A2A2A]/40 truncate">{user.businessName || 'Edit profile'}</p>
                </div>
              </button>
            </div>
          ) : user === false ? (
            <div className="flex gap-1">
              <button onClick={() => { navigate('/login'); setMobileOpen(false); }}
                className="flex-1 text-xs text-center py-1.5 rounded border border-[#2A2A2A]/20 text-[#2A2A2A]/60 hover:bg-[#FFC107]/20">
                Sign In
              </button>
              <button onClick={() => { navigate('/register'); setMobileOpen(false); }}
                className="flex-1 text-xs text-center py-1.5 rounded bg-[#E23D28] text-white">
                Register
              </button>
            </div>
          ) : null}
          <p className="text-xs text-center text-[#2A2A2A]/40 font-medium mt-2" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.1em' }}>
            Know Your Dough.
          </p>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(42,42,42,1)] text-white"
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r-2 border-[#2A2A2A] ${
          collapsed ? 'w-16' : 'w-56'
        } transition-all duration-200 flex-shrink-0`}
        data-testid="sidebar-desktop"
      >
        {navContent}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 text-[#2A2A2A]/50 hover:text-[#2A2A2A] transition-colors border-t-2 border-[#2A2A2A]"
          data-testid="sidebar-collapse-toggle"
        >
          <Menu className="w-4 h-4 mx-auto" />
        </button>
      </aside>

      {/* Sidebar - Mobile */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full z-40 flex flex-col bg-white border-r-2 border-[#2A2A2A] w-56 transform transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar-mobile"
      >
        {navContent}
      </aside>
    </>
  );
}
